'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'

import { Container } from '@/components/Container'

// Real Kingdom Kode client / case-study logos (dark variants for the light band).
import logoAcquisitionNetwork from '@/images/clients/acquisition-network/aqn-logo-long-dark.svg'
import logoRegen from '@/images/clients/regen-design-build/regendb-logo-long.svg'
import logoGreenLightGo from '@/images/clients/greenlight-go/glg-dark-text.svg'
import logoMillstoneForge from '@/images/clients/millstone-forge-inc/mfi-logo-dark.svg'
import logoSorta from '@/images/clients/sorta/sorta-dark.png'
import logoJodyMack from '@/images/clients/jody-mack/author.svg'
import logoGoogle from '@/images/clients/google/google.svg'
import logoAdobe from '@/images/clients/adobe/adobe.svg'
import logoGcu from '@/images/clients/grand-canyon-university/gcu-logo.svg'
import logoForeground from '@/images/clients/foreground-tv/foreground.jpg'

const allLogos = [
  { id: 'google', name: 'Google', img: logoGoogle },
  { id: 'adobe', name: 'Adobe', img: logoAdobe },
  { id: 'gcu', name: 'Grand Canyon University', img: logoGcu },
  { id: 'acquisition-network', name: 'Acquisition Network', img: logoAcquisitionNetwork },
  { id: 'regen', name: 'Regen Design Build', img: logoRegen },
  { id: 'greenlight-go', name: 'Green Light Go', img: logoGreenLightGo },
  // foreground.tv only ships a light-on-dark logo, so invert it for the light band.
  { id: 'foreground', name: 'foreground.tv', img: logoForeground, invert: true },
  { id: 'millstone-forge', name: 'Millstone & Forge', img: logoMillstoneForge },
  { id: 'sorta', name: 'Sorta', img: logoSorta },
  { id: 'jody-mack', name: 'Jody Mack', img: logoJodyMack },
]

const DISPLAY_COUNT = 10

const srcOf = (img) => (typeof img === 'string' ? img : img?.src)

// Trust band beneath the hero: the white (#f0efec) frame continues down into a
// logo cloud of real clients that quietly swaps one logo at a time, before the
// dark story section.
export default function TrustedStartups() {
  const [displayed, setDisplayed] = useState(allLogos.slice(0, DISPLAY_COUNT))

  useEffect(() => {
    if (allLogos.length <= DISPLAY_COUNT) return
    const interval = setInterval(() => {
      setDisplayed((current) => {
        const shown = new Set(current.map((l) => l.id))
        const available = allLogos.filter((l) => !shown.has(l.id))
        if (!available.length) return current
        const next = available[Math.floor(Math.random() * available.length)]
        const slot = Math.floor(Math.random() * current.length)
        const updated = [...current]
        updated[slot] = next
        return updated
      })
    }, 2200)
    return () => clearInterval(interval)
  }, [])

  return (
    <section className="bg-[#f0efec]">
      <Container className="max-w-7xl py-16 sm:py-20">
        <h2 className="text-center font-mono text-xs font-medium tracking-[0.22em] text-neutral-500 uppercase sm:text-sm">
          Trusted by fast-growing startups
        </h2>

        <div className="mx-auto mt-12 flex max-w-6xl flex-wrap items-center justify-center gap-x-10 gap-y-8 md:gap-x-16 md:gap-y-12">
          {displayed.map((logo, index) => (
            <div key={index} className="relative flex h-7 w-28 items-center justify-center md:h-8 md:w-32">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.img
                  key={logo.id}
                  src={srcOf(logo.img)}
                  alt={logo.name}
                  draggable={false}
                  initial={{ y: 16, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -16, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className={`absolute max-h-full max-w-full object-contain opacity-60 grayscale transition-[opacity,filter] duration-300 hover:opacity-100 hover:grayscale-0 ${logo.invert ? 'invert mix-blend-darken' : ''}`}
                />
              </AnimatePresence>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}
